"use client";

import { useState } from "react";

import { useTranslations } from "@i18n/provider";
import type { AgentAction } from "@lib/agent/actionToken";
import { lessonTypeAccent } from "@lib/learner/hubTints";
import { modalityToLessonType } from "@lib/learner/modality/modality";
import { GhostButton, PrimaryButton } from "@ui/buttons";

type ChipStatus = "pending" | "busy" | "confirmed" | "failed" | "dismissed";

/**
 * Confirm chip for a tutor-proposed board action (D-13). The D-14 guarantee
 * is structural: the ONLY fetch lives inside the "Do it" tap handler — the
 * chip never auto-fires, and it POSTs to the same authenticated board routes
 * the on-page buttons use. "Not now" dismisses silently (local state only).
 */
export function AgentActionChip({ action }: { action: AgentAction }) {
  const { t } = useTranslations();
  const [status, setStatus] = useState<ChipStatus>("pending");

  if (status === "dismissed") {
    return null;
  }

  const modalityLabel = t(`curriculum.radar.modality.${action.modality}`);
  const proposal =
    action.type === "regenerate_slot"
      ? t("agent.actionChip.regenerate", { modality: modalityLabel })
      : t("agent.actionChip.request", { modality: modalityLabel, subject: action.subject });

  const execute = async () => {
    setStatus("busy");
    try {
      const lessonType = modalityToLessonType(action.modality);
      const res =
        action.type === "regenerate_slot"
          ? await fetch("/api/curriculum/board/regenerate", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ lessonType }),
            })
          : await fetch("/api/curriculum/board/request", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ subject: action.subject, lessonType }),
            });
      setStatus(res.ok ? "confirmed" : "failed");
    } catch {
      setStatus("failed");
    }
  };

  return (
    <div
      aria-busy={status === "busy" ? true : undefined}
      className={`surface-card mt-1 max-w-[85%] rounded-[20px] p-4 ${lessonTypeAccent(
        modalityToLessonType(action.modality),
      )}`}
    >
      {status === "confirmed" ? (
        <p className="flex items-center gap-2 text-sm text-ink-soft">
          <span className="material-symbols-outlined text-[20px]" aria-hidden="true">
            check_circle
          </span>
          {t("agent.actionChip.done")}
        </p>
      ) : status === "failed" ? (
        <p className="text-sm text-ink-soft">{t("agent.actionChip.failed")}</p>
      ) : (
        <>
          <p className="text-base text-ink-body">{proposal}</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <PrimaryButton
              onClick={() => void execute()}
              disabled={status === "busy"}
              className="min-h-[44px]"
            >
              {t("agent.actionChip.doIt")}
            </PrimaryButton>
            <GhostButton
              onClick={() => setStatus("dismissed")}
              disabled={status === "busy"}
              className="min-h-[44px]"
            >
              {t("agent.actionChip.notNow")}
            </GhostButton>
          </div>
        </>
      )}
    </div>
  );
}
